import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
  computed,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Municipality } from '@api/models/municipality.model';
import { MunicipalitiesService } from '@api/services/municipalities.service';
import { AuthService } from '@auth/services/auth.service';
import { IconComponent } from '@ui/icon/icon.component';
import { InputComponent } from '@ui/input/input.component';
import { AppService } from '../../../app.service';
import { CommonModule } from '@angular/common';
import { debounceTime, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-municipality-switcher',
  standalone: true,
  imports: [CommonModule, IconComponent, InputComponent, ReactiveFormsModule],
  template: `
    @if (showSwitcher()) {
      <div class="relative">
        <button
          (click)="toggleMenu()"
          class="flex items-center gap-2 text-slate-600 hover:text-slate-800 p-2 rounded-full hover:bg-slate-100"
        >
          <ui-icon name="location_city" [size]="'base'"></ui-icon>
          <span class="text-sm">{{ currentMunicipalityName() }}</span>
          <ui-icon name="expand_more" [size]="'sm'"></ui-icon>
        </button>

        @if (isOpen()) {
          <div
            class="absolute max-h-80 overflow-hidden right-0 mt-1 w-72 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
          >
            <div class="p-2 border-b border-gray-100">
              <ui-input
                [formControl]="searchControl"
                placeholder="Zoek gemeente..."
                (click)="$event.stopPropagation()"
              />
            </div>
            <div class="max-h-60 overflow-y-auto">
              @for (municipality of filteredMunicipalities(); track municipality.id) {
                <button
                  (click)="selectMunicipality(municipality)"
                  class="flex w-full items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                  [class.bg-slate-50]="municipality.id === currentMunicipalityId()"
                >
                  <span class="flex-1">{{ municipality.name }}</span>
                  @if (municipality.id === currentMunicipalityId()) {
                    <ui-icon name="check" [size]="'sm'" class="text-primary-600"></ui-icon>
                  }
                </button>
              }
              @if (filteredMunicipalities().length === 0 && searchControl.value) {
                <div class="px-4 py-2 text-sm text-gray-500">
                  Geen gemeenten gevonden
                </div>
              }
            </div>
          </div>
        }
      </div>
    }
  `,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MunicipalitySwitcherComponent {
  private readonly municipalityService = inject(MunicipalitiesService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appService = inject(AppService);

  isAdmin = this.authService.isAdmin;
  municipalities = signal<Municipality[]>([]);
  isOpen = signal(false);

  currentMunicipalityId = computed(() => this.appService.municipalityId());
  currentMunicipalityName = computed(() => {
    const current = this.municipalities().find(m => m.id === this.currentMunicipalityId());
    return current?.name || 'Selecteer gemeente';
  });

  user = this.authService.currentUser;

  showSwitcher = computed(() => {
    const user = this.user();
    if (this.authService.isAdmin()) {
      return true;
    }
    if (user && user.municipalities.length > 1) {
      return true;
    }
    /** Backoffice: geen vaste gemeente; zelfde lijst als admin om context te kiezen. */
    if (this.authService.isBackoffice()) {
      return true;
    }
    return false;
  });

  searchControl = new FormControl('');
  searchTerm = signal('');

  filteredMunicipalities = computed(() => {
    const search = this.searchTerm().toLowerCase();
    if (!search) {
      return this.municipalities();
    }
    return this.municipalities().filter(municipality =>
      municipality.name.toLowerCase().includes(search)
    );
  });

  constructor() {
    effect(() => {
      if (this.showSwitcher()) {
        this.loadMunicipalities();
      }
    });

    // Reset search when menu closes
    effect(() => {
      if (!this.isOpen()) {
        this.searchControl.setValue('');
      }
    });

    // Subscribe to search changes
    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.searchTerm.set(value || '');
      });
  }

  private loadMunicipalities(): void {
    this.municipalityService
      .getMunicipalities(1, 100) // Get more municipalities for better search results
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(municipalities => {
        this.municipalities.set(municipalities.data);
      });
  }

  toggleMenu(): void {
    this.isOpen.update(value => !value);
  }

  selectMunicipality(municipality: Municipality): void {
    this.appService.setCurrentMunicipalityId(municipality.id);
    this.isOpen.set(false);
  }
}
